import { useCallback, useRef, useState } from 'react';

export default function useTextHistory(setValue) {
  const [snapshots, setSnapshots] = useState([]);
  const [pointer, setPointer] = useState(-1);
  const currentValueRef = useRef('');

  const setCurrentValue = useCallback((value) => {
    currentValueRef.current = value;
  }, []);

  const pushSnapshot = useCallback((text) => {
    setSnapshots((prev) => {
      const base = pointer >= 0 ? prev.slice(0, pointer + 1) : prev;
      return [...base, text];
    });
    setPointer(-1);
  }, [pointer]);

  const canUndo = pointer === -1 ? snapshots.length > 0 : pointer > 0;
  const canRedo = pointer >= 0 && pointer < snapshots.length - 1;

  const undo = useCallback(() => {
    if (!canUndo) return null;

    if (pointer === -1) {
      const current = currentValueRef.current;
      setSnapshots((prev) => [...prev, current]);
      const target = snapshots.length - 1;
      const targetValue = snapshots[target];
      setValue(targetValue);
      currentValueRef.current = targetValue;
      setPointer(target);
      return targetValue;
    }

    const target = pointer - 1;
    const targetValue = snapshots[target];
    setValue(targetValue);
    currentValueRef.current = targetValue;
    setPointer(target);
    return targetValue;
  }, [canUndo, pointer, snapshots, setValue]);

  const redo = useCallback(() => {
    if (!canRedo) return null;
    const target = pointer + 1;
    const targetValue = snapshots[target];
    setValue(targetValue);
    currentValueRef.current = targetValue;
    setPointer(target);
    return targetValue;
  }, [canRedo, pointer, snapshots, setValue]);

  return {
    canUndo,
    canRedo,
    pushSnapshot,
    undo,
    redo,
    setCurrentValue,
  };
}
